import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, REFUND_POLICY_TABS, REFUND_POLICY_MESSAGES, REFUND_POLICY_COLUMNS, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl,
  REFUND_POLICY_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

// ── Scrollable table wrapper with left/right scroll buttons ────────────

function TableWithScrollButtons({ children }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) {
      setShowLeft(el.scrollLeft > 5);
      setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
      ro.disconnect();
    };
  }, [checkScroll]);

  const scrollTo = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="AdminTableScrollWrap">
      <div className="AdminScrollBar">
          <button className="AdminScrollBtn" onClick={() => scrollTo("left")} disabled={!showLeft} title="Scroll left">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="10 4 6 8 10 12" />
            </svg>
          </button>
          <button className="AdminScrollBtn" onClick={() => scrollTo("right")} disabled={!showRight} title="Scroll right">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 4 10 8 6 12" />
            </svg>
          </button>
        </div>
      <div ref={scrollRef} className="AdminTableScroll">
        {children}
      </div>
    </div>
  );
}

const REFUND_TYPE_OPTIONS = ["Full", "Partial", "None"];
const REFUND_STATUS_OPTIONS = [
  "Service Fee Waived",
  "No Refund",
  "10%", "20%", "30%", "40%", "50%",
  "60%", "70%", "80%", "90%", "100%"
];

// Policy stage columns used in the form modal
const POLICY_STAGE_COLUMNS = [
  { key: "stage", label: "Stage" },
  { key: "substage", label: "Substage" },
  { key: "refundType", label: "Type", type: "combobox", options: REFUND_TYPE_OPTIONS },
  { key: "refundStatus", label: "Status", type: "combobox", options: REFUND_STATUS_OPTIONS },
  { key: "reason", label: "Reason", type: "textarea" },
];

function AdminRefundPolicyEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("productTabs");
  const [tabOrder, setTabOrder] = useState(REFUND_POLICY_TABS.map(t => t.id));
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewModalText, setReviewModalText] = useState("");

  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const [stageSearch, setStageSearch] = useState({});
  const [stageSearchOpen, setStageSearchOpen] = useState({});
  const stageSearchRefs = useRef({});
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    const openTabId = Object.keys(stageSearchOpen).find((id) => stageSearchOpen[id]);
    if (openTabId && stageSearchRefs.current[openTabId]) {
      stageSearchRefs.current[openTabId].focus();
    }
  }, [stageSearchOpen]);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
    setStageSearch({});
    setStageSearchOpen({});
  }, [activeTab]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  // Row form modal state
  const [showRowForm, setShowRowForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState({});
  const [pendingInsertIndex, setPendingInsertIndex] = useState(null);
  const snapshotRef = useRef(null);
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        setContent(data.section.content);
        snapshotRef.current = JSON.stringify(data.section.content);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(REFUND_POLICY_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(REFUND_POLICY_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const getSheetId = (url) => {
    const m = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
    return m ? m[1] : url;
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    const sheetUrl = getSheetUrl(section.key);
    const sheetId = getSheetId(sheetUrl);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/refundPolicy/push-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content, sheetUrl, sheetId }),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Push response:", res.status, text);
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      console.error("Push error:", err);
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    const sheetUrl = getSheetUrl(section.key);
    const sheetId = getSheetId(sheetUrl);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/refundPolicy/pull-sheet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sheetUrl, sheetId }),
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteRow = async (field, index) => {
    const item = content?.[field]?.[index];
    if (!item) return;

    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = updated[field].filter((_, i) => i !== index);
      if (field === "productTabs" && item.id) {
        const policyStagesCopy = { ...updated.policyStages };
        delete policyStagesCopy[item.id];
        updated.policyStages = policyStagesCopy;
      }
      return updated;
    });

    if (field === "testimonials" && item.id != null) {
      const token = sessionStorage.getItem("tvc_token");
      try {
        const res = await fetch(`${API_BASE_URL}/refund/testimonials/${item.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setMessage("Testimonial deleted and review rejected.");
        } else {
          setMessage("Delete error: " + (data.error || res.status));
        }
      } catch (err) {
        setMessage("Delete error: " + err.message);
      }
    }
  };

  const handleDeletePolicyStage = (tabId, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const stages = updated.policyStages?.[tabId]?.filter((_, i) => i !== index) || [];
      updated.policyStages = { ...updated.policyStages, [tabId]: stages };
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...updated[field]];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddArrayRowBefore = (field, index, columns) => {
    setPendingInsertIndex(index);
    handleOpenAddArrayForm(field);
  };

  const handleAddArrayRowAfter = (field, index, columns) => {
    setPendingInsertIndex(index + 1);
    handleOpenAddArrayForm(field);
  };

  const handleMovePolicyStageUp = (tabId, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const stages = [...(updated.policyStages?.[tabId] || [])];
      [stages[index - 1], stages[index]] = [stages[index], stages[index - 1]];
      updated.policyStages = { ...updated.policyStages, [tabId]: stages };
      return updated;
    });
  };

  const handleMovePolicyStageDown = (tabId, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const stages = [...(updated.policyStages?.[tabId] || [])];
      if (index >= stages.length - 1) return prev;
      [stages[index], stages[index + 1]] = [stages[index + 1], stages[index]];
      updated.policyStages = { ...updated.policyStages, [tabId]: stages };
      return updated;
    });
  };

  const handleAddPolicyStageBefore = (tabId, index) => {
    setPendingInsertIndex(index);
    handleOpenAddStageForm(tabId);
  };

  const handleAddPolicyStageAfter = (tabId, index) => {
    setPendingInsertIndex(index + 1);
    handleOpenAddStageForm(tabId);
  };

  // ── Row Form Modal handlers ─────────────────────────────────────────

  const handleOpenAddStageForm = (tabId) => {
    setEditingField("policyStages");
    setEditingTabId(tabId);
    setEditingIndex(null);
    setEditingItem({ stage: "", substage: "", refundType: "", refundStatus: "", reason: "" });
    setShowRowForm(true);
  };

  const handleOpenEditStageForm = (tabId, index) => {
    const item = content?.policyStages?.[tabId]?.[index];
    if (!item) return;
    setEditingField("policyStages");
    setEditingTabId(tabId);
    setEditingIndex(index);
    setEditingItem({ ...item });
    setShowRowForm(true);
  };

  const handleOpenAddArrayForm = (field) => {
    const columns = REFUND_POLICY_COLUMNS[field];
    const emptyRow = {};
    if (columns) {
      for (const col of columns) {
        emptyRow[col.key] = "";
      }
    }
    setEditingField(field);
    setEditingTabId(null);
    setEditingIndex(null);
    setEditingItem(emptyRow);
    setShowRowForm(true);
  };

  const handleOpenEditArrayForm = (field, index) => {
    const item = content?.[field]?.[index];
    if (!item) return;
    setEditingField(field);
    setEditingTabId(null);
    setEditingIndex(index);
    setEditingItem({ ...item });
    setShowRowForm(true);
  };

  const handleCloseRowForm = () => {
    setShowRowForm(false);
    setEditingField(null);
    setEditingTabId(null);
    setEditingIndex(null);
    setEditingItem({});
    setPendingInsertIndex(null);
  };

  const handleRowFormFieldChange = (key, value) => {
    setEditingItem((prev) => {
      const updated = { ...prev, [key]: value };
      if (editingField === "testimonials" && key === "name") {
        updated.initials = value
          .split(/\s+/)
          .filter(Boolean)
          .map((w) => w[0].toUpperCase())
          .join("");
      }
      return updated;
    });
  };

  const handleSaveRowForm = () => {
    if (editingField === "policyStages") {
      const tabId = editingTabId;
      setContent((prev) => {
        const updated = { ...prev };
        const stages = [...(updated.policyStages?.[tabId] || [])];
        if (editingIndex === null) {
          if (pendingInsertIndex !== null) {
            stages.splice(pendingInsertIndex, 0, { id: Date.now(), ...editingItem });
          } else {
            stages.push({ id: Date.now(), ...editingItem });
          }
        } else {
          stages[editingIndex] = { ...stages[editingIndex], ...editingItem };
        }
        updated.policyStages = { ...updated.policyStages, [tabId]: stages };
        return updated;
      });
    } else {
      const field = editingField;
      setContent((prev) => {
        const updated = { ...prev };
        const arr = [...(updated[field] || [])];
        if (editingIndex === null) {
          if (pendingInsertIndex !== null) {
            arr.splice(pendingInsertIndex, 0, { id: Date.now(), ...editingItem });
          } else {
            arr.push({ id: Date.now(), ...editingItem });
          }
        } else {
          arr[editingIndex] = { ...arr[editingIndex], ...editingItem };
        }
        updated[field] = arr;
        return updated;
      });
    }
    handleCloseRowForm();
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, REFUND_POLICY_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => b.duplicateIndex - a.duplicateIndex);
    for (const dup of sorted) {
      if (dup.tabKeyField) {
        const stages = cleanContent[dup.field]?.[dup.tabKeyField] || [];
        if (stages[dup.duplicateIndex]) {
          stages.splice(dup.duplicateIndex, 1);
          cleanContent[dup.field] = { ...cleanContent[dup.field], [dup.tabKeyField]: stages };
        }
      } else {
        const arr = [...(cleanContent[dup.field] || [])];
        if (arr[dup.duplicateIndex]) {
          arr.splice(dup.duplicateIndex, 1);
          cleanContent[dup.field] = arr;
        }
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);

    const token = sessionStorage.getItem("tvc_token");

    const submitPayload = overrideContent || { ...content };

    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });

      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
        snapshotRef.current = JSON.stringify(content);
        // Auto-push to sheet for Admin users
        if (role === "Admin") {
          fetch(`${API_BASE_URL}/content/refundPolicy/push-sheet`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }
      } else {
        console.error("Submit failed:", res.status, text);
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderArrayEditor = (field, columns) => {
    const items = content?.[field] || [];

    const displayValue = (item, col) => {
      const val = item[col.key];
      if (!val || val === "") return "—";
      return val;
    };

    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div className="AdminSectionHeaderActions">
            <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRef}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={arraySearch}
                onChange={(e) => setArraySearch(e.target.value)}
                onBlur={() => { setArraySearch(""); setTimeout(() => setSearchOpen(false), 200) }}
              />
              {!searchOpen && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleOpenAddArrayForm(field)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <TableWithScrollButtons>
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!arraySearch.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={item.id || idx}
                  style={{ transition: "filter 0.15s" }}
                  draggable="true"
                  onDragStart={(e) => {
                    e.stopPropagation();
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.outline = "";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveRowUp(field, idx)}
                        disabled={idx === 0}
                        title="Move up"
                      >
                        &#8593;
                      </button>
                      <button
                        className="AdminMoveBtn"
                        onClick={() => handleMoveRowDown(field, idx)}
                        disabled={idx === items.length - 1}
                        title="Move down"
                      >
                        &#8595;
                      </button>
                      <button
                        className="AdminEditBtn"
                        onClick={() => handleOpenEditArrayForm(field, idx)}
                        title="Edit row"
                      >
                        &#9998;
                      </button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.key === "review" && item[col.key] ? (
                        <span
                          style={{ cursor: "pointer", color: "#2563eb", textDecoration: "underline", textDecorationStyle: "dotted" }}
                          onClick={() => { setReviewModalText(item[col.key]); setShowReviewModal(true); }}
                        >
                          {item[col.key].length > 80 ? item[col.key].substring(0, 80) + "..." : item[col.key]}
                        </span>
                      ) : (
                        <span className="AdminTableCellText">{displayValue(item, col)}</span>
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddArrayRowBefore(field, idx, columns)}
                        title="Add row before"
                      >
                        &#9650;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnBlue"
                        onClick={() => handleAddArrayRowAfter(field, idx, columns)}
                        title="Add row after"
                      >
                        &#9660;+
                      </button>
                      <button
                        className="AdminIconBtn AdminIconBtnRed"
                        onClick={() => handleDeleteRow(field, idx)}
                        title="Delete row"
                      >
                        &#10005;
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWithScrollButtons>
        {items.length === 0 && (
          <div className="AdminEmptyState">{REFUND_POLICY_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && arraySearch.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(arraySearch.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{arraySearch}"</div>
        )}
      </div>
    );
  };

  const renderPolicyStagesEditor = () => {
    const tabs = content?.productTabs || [];
    return tabs.map((tab, tabIdx) => {
      const stages = content?.policyStages?.[tab.id] || [];
      const tabSearch = stageSearch[tab.id] || "";
      const tabSearchOpen = stageSearchOpen[tab.id] || false;
      const filteredStages = tabSearch.trim()
        ? stages.filter((s) =>
            [s.stage, s.substage, s.refundType, s.refundStatus, s.reason].some(
              (v) => v && v.toLowerCase().includes(tabSearch.trim().toLowerCase())
            )
          )
        : stages;

      const displayVal = (val) => {
        if (!val || val === "") return "—";
        return val;
      };

      return (
        <div key={tab.id || tabIdx} className="AdminCardMb">
          <div className="AdminSectionHeader">
            <h4 className="AdminSectionTitle">
              {tab.label || tab.id} Policy Stages
            </h4>
            <div className="AdminSectionHeaderActions">
              <div className={`AdminPillSearchWrap ${tabSearchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={(el) => { stageSearchRefs.current[tab.id] = el; }}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search..."
                  value={tabSearch}
                  onChange={(e) => setStageSearch((prev) => ({ ...prev, [tab.id]: e.target.value }))}
                   onBlur={() => { setStageSearch((prev) => ({ ...prev, [tab.id]: "" })); setTimeout(() => setStageSearchOpen((prev) => ({ ...prev, [tab.id]: false })), 200) }}
                />
                {!tabSearchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setStageSearchOpen((prev) => ({ ...prev, [tab.id]: true }))} type="button" aria-label="Search" />
                )}
              </div>
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
                onClick={() => handleOpenAddStageForm(tab.id)}
              >
                + Add Stage
              </button>
            </div>
          </div>
          <TableWithScrollButtons>
            <table className="AdminTable AdminTableEditor">
              <thead>
                <tr>
                  <th className="AdminThOrder">Order</th>
                  <th>Stage</th>
                  <th>Substage</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th className="AdminThActions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStages.map((stage, idx) => (
                  <tr
                    key={stage.id || idx}
                    draggable="true"
                    onDragStart={(e) => {
                      dragRef.current = { type: "policyStage", tabId: tab.id, fromIndex: idx };
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                      e.currentTarget.style.opacity = "0.4";
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      e.currentTarget.style.outline = "2px dashed #3b82f6";
                      e.currentTarget.style.outlineOffset = "-2px";
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.style.outline = "";
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      if (!dragRef.current || dragRef.current.type !== "policyStage" || dragRef.current.tabId !== tab.id) { dragRef.current = null; return; }
                      const { fromIndex } = dragRef.current;
                      if (fromIndex === idx) { dragRef.current = null; return; }
                      setContent((prev) => {
                        const updated = { ...prev };
                        const stgs = [...(updated.policyStages?.[tab.id] || [])];
                        const [moved] = stgs.splice(fromIndex, 1);
                        stgs.splice(idx, 0, moved);
                        updated.policyStages = { ...updated.policyStages, [tab.id]: stgs };
                        return updated;
                      });
                      dragRef.current = null;
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = "";
                      e.currentTarget.style.outline = "";
                      dragRef.current = null;
                    }}
                  >
                    <td>
                      <div className="AdminOrderBtns">
                        <button
                          className="AdminMoveBtn"
                          onClick={() => handleMovePolicyStageUp(tab.id, idx)}
                          disabled={idx === 0}
                          title="Move up"
                        >
                          &#8593;
                        </button>
                        <button
                          className="AdminMoveBtn"
                          onClick={() => handleMovePolicyStageDown(tab.id, idx)}
                          disabled={idx === stages.length - 1}
                          title="Move down"
                        >
                          &#8595;
                        </button>
                        <button
                          className="AdminEditBtn"
                          onClick={() => handleOpenEditStageForm(tab.id, idx)}
                          title="Edit stage"
                        >
                          &#9998;
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="AdminTableCellText">{displayVal(stage.stage)}</span>
                    </td>
                    <td>
                      <span className="AdminTableCellText">{displayVal(stage.substage)}</span>
                    </td>
                    <td>
                      <span className="AdminTableCellText">{displayVal(stage.refundType)}</span>
                    </td>
                    <td>
                      <span className="AdminTableCellText">{displayVal(stage.refundStatus)}</span>
                    </td>
                    <td>
                      <span className="AdminTableCellText">{displayVal(stage.reason)}</span>
                    </td>
                    <td>
                      <div className="AdminActionBtns">
                        <button
                          className="AdminIconBtn AdminIconBtnBlue"
                          onClick={() => handleAddPolicyStageBefore(tab.id, idx)}
                          title="Add row before"
                        >
                          &#9650;+
                        </button>
                        <button
                          className="AdminIconBtn AdminIconBtnBlue"
                          onClick={() => handleAddPolicyStageAfter(tab.id, idx)}
                          title="Add row after"
                        >
                          &#9660;+
                        </button>
                        <button
                          className="AdminIconBtn AdminIconBtnRed"
                          onClick={() => handleDeletePolicyStage(tab.id, idx)}
                          title="Delete stage"
                        >
                          &#10005;
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWithScrollButtons>
          {stages.length === 0 && !tabSearch.trim() && (
            <div className="AdminEmptyState">{REFUND_POLICY_MESSAGES.emptyArray}</div>
          )}
          {stages.length > 0 && tabSearch.trim() && filteredStages.length === 0 && (
            <div className="AdminEmptyState">No results found for "{tabSearch}"</div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return <div className="AdminEmptyState">{REFUND_POLICY_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{REFUND_POLICY_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;
  const orderedTabs = [...REFUND_POLICY_TABS].sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Refund Policy Content
          {activeTab !== "data" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div className="AdminHeaderActionsRow">
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn AdminNowrap" onClick={handleSubmit} disabled={!isDirty || saving}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button
            className="AdminButton AdminButtonSmall AdminSheetBtn"
            onClick={handleOpenSheet}
          >
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminTabBar" ref={tabBarRef} style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {orderedTabs.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { dragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = dragRef.current;
              if (!fromId || fromId === tab.id) return;
              const newOrder = [...tabOrder];
              const fromIdx = newOrder.indexOf(fromId);
              const toIdx = newOrder.indexOf(tab.id);
              newOrder.splice(fromIdx, 1);
              newOrder.splice(toIdx, 0, fromId);
              setTabOrder(newOrder);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; dragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">
              Data
            </h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">
                {section?.key || "refundPolicy"}
              </p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(REFUND_POLICY_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "productTabs" && renderArrayEditor("productTabs", REFUND_POLICY_COLUMNS.productTabs)}

      {activeTab === "policyStages" && renderPolicyStagesEditor()}

      {activeTab === "testimonials" && renderArrayEditor("testimonials", REFUND_POLICY_COLUMNS.testimonials)}

      {showRowForm && (
        <div className="AdminModalOverlayFixed">
          <div className="AdminModalContentBox">
            <button
              onClick={handleCloseRowForm}
              className="AdminModalCloseBtn"
              title="Close"
            >
              &#10005;
            </button>
            <h3 className="AdminModalFormTitle">
              {editingIndex === null ? 'Add' : 'Edit'}{" "}
              {editingField === "policyStages"
                ? "Policy Stage"
                : editingField === "productTabs" ? "Product Tab"
                : editingField === "testimonials" ? "Testimonial"
                : "Row"}
            </h3>
            <p className="AdminModalFormSubtitle">
              Fill in the details and press Continue to add it to the table.
            </p>
            <div className="AdminModalFormGrid">
              {(editingField === "policyStages" ? POLICY_STAGE_COLUMNS : REFUND_POLICY_COLUMNS[editingField] || []).map((col) => {
                const val = editingItem[col.key] || "";
                return (
                  <div key={col.key} className={
                    editingField === "productTabs" ? "AdminModalFormGridCenter" :
                    col.type === "textarea" ? "AdminModalFormGridFull" :
                    ""
                  }>
                    <label className="AdminModalFormLabel">
                      {col.label}
                    </label>
                    {col.type === "textarea" ? (
                      <div>
                        <textarea
                          className="AdminInput AdminModalFormTextarea"
                          value={val}
                          onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                          placeholder={col.label}
                        />
                        <span className="AdminModalFormCharCount">
                          {val.length} chars
                        </span>
                      </div>
                    ) : col.type === "select" || col.type === "combobox" ? (
                      col.type === "combobox" ? (
                        <div>
                          <input
                            className="AdminInput AdminModalFormInput"
                            value={val}
                            onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                            placeholder={col.label}
                            list={`${col.key}-list`}
                          />
                          <datalist id={`${col.key}-list`}>
                            {col.options.map((opt) => (
                              <option key={opt} value={opt} />
                            ))}
                          </datalist>
                        </div>
                      ) : (
                        <select
                          className="AdminInput AdminModalFormInput"
                          value={val}
                          onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                        >
                          <option value="">Select {col.label}</option>
                          {col.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )
                    ) : (
                      <input
                        className="AdminInput AdminModalFormInput"
                        value={val}
                        onChange={(e) => handleRowFormFieldChange(col.key, e.target.value)}
                        placeholder={col.label}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="AdminModalBtnRow">
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleSaveRowForm}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModalOverlayFixed">
          <div className="AdminModalContentBoxNarrow">
            <h3>Add a note for the approver</h3>
            <textarea
              className="AdminNoteTextarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Describe your changes..."
            />
            <div className="AdminNoteModalRow">
              <button
                className="AdminButton AdminButtonSmall AdminButtonSecondary"
                onClick={() => setShowNoteModal(false)}
              >
                Cancel
              </button>
              <button
                className="AdminButton AdminButtonSmall"
                onClick={handleConfirmSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent AdminModalMaxW520" style={{position:"relative"}}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div className="AdminText36 AdminMb8">&#9888;</div>
            <h3 className="AdminModalFormTitle">Duplicate Entries Detected</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B16">
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div className="AdminDupWarningBox">
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  const prefix = d.sectionHeading ? `[${d.sectionHeading}] ` : "";
                  const suffix = d.tabKeyField ? ` (in ${d.tabKeyField})` : "";
                  const dispVal = d.duplicateValue || "Row #" + (d.duplicateIndex + 1) + suffix;
                  byTab[d.tabId].items.push(prefix + dispVal);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} className="AdminDupItem">
                    <strong className="AdminTextDark">{info.label}</strong>
                    <span className="AdminTextSlate"> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span className="AdminTextDark">{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div className="AdminFlexCenterG12">
              <button
                className="AdminButton AdminSubmitBtn AdminBtnDanger"
                onClick={handleRemoveDuplicates}
                disabled={duplicateActionLoading}
              >
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={handleContinueAnyway}
                disabled={duplicateActionLoading}
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent AdminModalMaxW400" onClick={e => e.stopPropagation()}>
            <div className="AdminText48">
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle AdminMb4">{successTitle}</h3>
            <p className="AdminText13 AdminTextMuted AdminM0B20">
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div className="AdminFlexCenter">
              <button
                className="AdminButton AdminSubmitBtn"
                onClick={() => setShowSuccessOverlay(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Full Review Modal ── */}
      {showReviewModal && (
        <div className="AdminModal" onClick={() => setShowReviewModal(false)}>
          <div className="AdminModalContent AdminModalMaxW520" onClick={(e) => e.stopPropagation()}>
            <h3 className="AdminModalTitle">Review</h3>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 400, overflowY: "auto" }}>
              {reviewModalText}
            </div>
            <div className="AdminFlexCenter" style={{ marginTop: 20 }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowReviewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminRefundPolicyEditor;
